import React, { useState, useEffect } from 'react';
import { Power, PowerOff, Loader2, AlertTriangle } from 'lucide-react';
import { io, Socket } from 'socket.io-client';
import { API_CONFIG } from '@/constants/api';
import { getAuthToken } from '@/utils/auth';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface AvailabilityToggleProps {
  userId: string;
  initialAvailable: boolean;
  isApproved: boolean;
  approvalStatus?: string;
  initialModes?: string[];
}

export const AvailabilityToggle: React.FC<AvailabilityToggleProps> = ({ 
  userId, 
  initialAvailable,
  isApproved,
  approvalStatus,
  initialModes = []
}) => {
  const [isAvailable, setIsAvailable] = useState(initialAvailable);
  const [isLoading, setIsLoading] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const safeInitialModes = Array.isArray(initialModes) ? initialModes : [];
  const [selectedModes, setSelectedModes] = useState<string[]>(safeInitialModes);
  const [currentModes, setCurrentModes] = useState<string[]>(safeInitialModes);
  const [showApprovalError, setShowApprovalError] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    const newSocket = io(`${API_CONFIG.BASE_URL.replace('/api', '')}/consultation`, {
      query: { userId, token },
      transports: ['websocket'],
    });

    newSocket.on('connect', () => {
      console.log('Connected to consultation namespace');
    });

    newSocket.on('availabilityChanged', (data: { isAvailable: boolean; onCallModes?: string[] }) => {
      setIsAvailable(data.isAvailable);
      if (data.onCallModes && Array.isArray(data.onCallModes)) {
        setCurrentModes(data.onCallModes);
        setSelectedModes(data.onCallModes);
      } else {
        setCurrentModes([]);
        setSelectedModes([]);
      }
      setIsLoading(false);
    });

    newSocket.on('error', (data: { message: string }) => {
      alert(data.message);
      setIsLoading(false);
    });

    newSocket.on('availabilityError', (data: { message: string; currentState?: boolean; currentModes?: string[] }) => {
      toast.error(data.message);
      if (data.currentState !== undefined) setIsAvailable(data.currentState);
      if (data.currentModes !== undefined) {
        setCurrentModes(data.currentModes);
        setSelectedModes(data.currentModes);
      }
      setIsLoading(false);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [userId]);

  const handleToggleClick = () => {
    if (!socket || isLoading) return;

    if (!isAvailable) {
      if (!isApproved) {
        setShowApprovalError(true);
        return;
      }
      setIsModalOpen(true);
    } else {
      // Optimistic UI update
      setIsAvailable(false);
      setCurrentModes([]);
      setIsLoading(true);
      socket.emit('toggleAvailability', { userId, isAvailable: false });
    }
  };

  const confirmActivation = () => {
    if (selectedModes.length === 0) return;
    
    // Optimistic UI update
    setIsAvailable(true);
    setCurrentModes(selectedModes);
    setIsLoading(true);
    socket?.emit('toggleAvailability', { 
      userId, 
      isAvailable: true, 
      onCallModes: selectedModes 
    });
    setIsModalOpen(false);
  };

  const toggleMode = (mode: string) => {
    setSelectedModes(prev => 
      prev.includes(mode) 
        ? prev.filter(m => m !== mode) 
        : [...prev, mode]
    );
  };

  const getStatusText = () => {
    if (!isAvailable) return 'Modo Plantão: Inativo';
    
    if (currentModes.length === 0) return 'Modo Plantão: Ativo';

    const modesText = currentModes
      .map(m => m === 'TELEMEDICINE' ? 'Telemedicina' : 'Atendimento domiciliar')
      .join(' e ');
      
    return `Modo Plantão: Ativo — ${modesText}`;
  };

  return (
    <>
      <button
        onClick={handleToggleClick}
        disabled={isLoading}
        className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all duration-300 shadow-sm ${
          isAvailable
            ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
        }`}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : isAvailable ? (
          <Power className="w-4 h-4" />
        ) : (
          <PowerOff className="w-4 h-4" />
        )}
        <span className="text-sm">
          {getStatusText()}
        </span>
        {isAvailable && (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
        )}
      </button>

      {/* Modal de Seleção de Modalidade */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Ativar Modo Plantão</DialogTitle>
            <DialogDescription>
              Escolha em quais modalidades você deseja ficar disponível para atendimento imediato.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="flex items-center space-x-3 space-y-0">
              <Checkbox 
                id="telemedicine" 
                checked={selectedModes.includes('TELEMEDICINE')}
                onCheckedChange={() => toggleMode('TELEMEDICINE')}
              />
              <Label htmlFor="telemedicine" className="font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                Telemedicina
              </Label>
            </div>
            <div className="flex items-center space-x-3 space-y-0">
              <Checkbox 
                id="homecare" 
                checked={selectedModes.includes('HOME_CARE')}
                onCheckedChange={() => toggleMode('HOME_CARE')}
              />
              <Label htmlFor="homecare" className="font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                Atendimento domiciliar
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button 
              onClick={confirmActivation} 
              disabled={selectedModes.length === 0}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              Ativar Plantão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Erro de Aprovação */}
      <Dialog open={showApprovalError} onOpenChange={setShowApprovalError}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <DialogTitle className="text-center">
              {approvalStatus === 'REJECTED' ? 'Cadastro não aprovado' : 'Aguardando Aprovação'}
            </DialogTitle>
            <DialogDescription className="text-center pt-2">
              {approvalStatus === 'REJECTED'
                ? 'Seu cadastro médico não foi aprovado no momento. Para mais informações, entre em contato com o suporte.'
                : 'Envie seus documentos para análise e aguarde aprovação para ativar o Modo Plantão.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button onClick={() => setShowApprovalError(false)} className="bg-teal-600 hover:bg-teal-700 text-white w-full sm:w-auto">
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
